import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { TrialNotesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as NoteTypesNotesActions from "../../../actions/noteTypeNotes";
import * as TrialActions from "../../../actions/trial";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import { TrialLayout } from "../../index";

import "./TrialNotesPage.scss";

interface TrialNotesPageProps {
    trial: Dtos.Trial;
    loadingTrial: boolean;
    loadTrialSuccess: boolean;
    loadTrialFailure: boolean;
    loadTrialErrorCode: string;
    noteTypeNotes: Dtos.NoteTypeNotes[];
    loadingNoteTypeNotes: boolean;
    loadNoteTypeNotesSuccess: boolean;
    loadNoteTypeNotesFailure: boolean;
    loadNoteTypeNotesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrial: TrialActions.ITrialClearActionCreator;
    loadTrial: TrialActions.ITrialLoadByIdActionCreator;
    clearNoteTypeNotes: NoteTypesNotesActions.INoteTypeNotesClearActionCreator;
    loadNoteTypeNotes: NoteTypesNotesActions.INoteTypeNotesLoadByTrialIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialNotesPage: FC<TrialNotesPageProps> = ({
    trial,
    loadingTrial,
    loadTrialSuccess,
    loadTrialFailure,
    loadTrialErrorCode,
    noteTypeNotes,
    loadingNoteTypeNotes,
    loadNoteTypeNotesSuccess,
    loadNoteTypeNotesFailure,
    loadNoteTypeNotesErrorCode,
    permissions,
    clearTrial,
    loadTrial,
    clearNoteTypeNotes,
    loadNoteTypeNotes,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const trialIdNumber = parseInt(trialId, 10);

    //
    // -- States --
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showTrialNotes = !loadingTrial && loadTrialSuccess && !loadingNoteTypeNotes && loadNoteTypeNotesSuccess && !creatingTree;


    // Load trial & notes
    useEffect(() => {
        if (trialIdNumber && !trial) {
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));
            setBreadcrumbs(TrialNotesBreadcrumbs(trialIdNumber));

            setTreeData(null);

            loadTrial(trialIdNumber);
            loadNoteTypeNotes(trialIdNumber);
        }
    }, [
        trialIdNumber,
        trial,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadTrial,
        setTreeData,
        TrialSubMenu,
        TrialNotesBreadcrumbs,
        loadNoteTypeNotes
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialFailure) {
            navigate(`/error/${loadTrialErrorCode}`);
        }
        if (loadNoteTypeNotesFailure) {
            navigate(`/error/${loadNoteTypeNotesErrorCode}`);
        }
    }, [
        loadTrialFailure, 
        loadTrialErrorCode, 
        loadNoteTypeNotesFailure, 
        loadNoteTypeNotesErrorCode, 
        navigate
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialIdNumber && trial?.displayName) {
            setBreadcrumbs(
                TrialNotesBreadcrumbs(trialIdNumber, trial.displayName)
            );
        }
    }, [
        trial,
        trialIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        TrialNotesBreadcrumbs,
    ]);

    // Cleanup effect to clear the trial on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearTrial();
            clearNoteTypeNotes();
        };
    }, [clearTrial, clearNoteTypeNotes, setTreeData]);

    // Update tree
    useEffect(() => {
        setInitalizedTreeData(false);
    }, [noteTypeNotes, setInitalizedTreeData]);

    // Create tree
    useEffect(() => {
        if (noteTypeNotes && !initalizedTreeData) {
            setCreatingTree(true);

            // If no trial notes return
            if (noteTypeNotes?.length == 0) {
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (notes) => {
                let children = [];

                children =
                    notes
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `note-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: " 1fr 3fr 8fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={FontAwesomeIcons.Duotone.NOTE}
                                        />
                                    </div>
                                    <div className="px-1">{y?.title}</div>
                                    <div className="d-inline-block px-1">
                                        <span
                                            className="text-truncate"
                                            style={{ whiteSpace: "pre-line" }}
                                        >
                                            {y?.comments}
                                        </span>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 8fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Title</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Comment</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                noteTypeNotes
                    ?.sort((a, b) =>
                        a?.noteType?.value.localeCompare(b?.noteType?.value)
                    )
                    ?.map((x) => ({
                        id: `type-${x.noteTypeId}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.NOTESMEDICAL}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "180px" }}
                                >
                                    {x.noteType?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary mb-0">
                                        {x?.notes?.length}
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.notes)],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        noteTypeNotes,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("note") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.TrialNoteView
                    )
                ) {
                    navigate(
                        `/trials/${trialIdNumber}/notes/${id.replace(
                            "note-",
                            ""
                        )}`
                    );
                }
                if (id.includes("type")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, trialIdNumber, permissions]
    );

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-9 mb-2"}>
                        <h1>Trial Notes</h1>
                    </div>
                    <div className="col-3" style={{ textAlign: "right" }}>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialNoteCreate,
                            trialIdNumber,
                            null
                        ) &&
                            trial &&
                            trial.id > 0 && (
                                <Link
                                    className={"btn btn-primary"}
                                    url={`/trials/${trial.id}/notes/create`}
                                >
                                    Create
                                </Link>
                            )}
                    </div>
                </div>
                {!showTrialNotes ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialNotes ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no trial notes
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        permissions,
        trialIdNumber,
        trial,
        showTrialNotes,
        treeData,
        handleToggle,
        toggleState,
    ]);

    return (
        <TrialLayout
            loading={loadingTrial || loadingNoteTypeNotes}
            permission={Dtos.Permission.TrialNoteView}
        >
            {renderContent}
        </TrialLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialNotesPageProps> => {
    let trial: Dtos.Trial | undefined = !(
        state.trials.formData instanceof Array
    )
        ? state.trials.formData
        : undefined;
    let noteTypeNotes: Dtos.NoteTypeNotes[] | undefined = (state.noteTypeNotes.formData instanceof Array)
        ? state.noteTypeNotes.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trial: trial!,
        loadingTrial:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Pending,
        loadTrialSuccess:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Success,
        loadTrialFailure:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Failure,
        loadTrialErrorCode: state.trials.loadState.errorCode,
        noteTypeNotes: noteTypeNotes!,
        loadingNoteTypeNotes:
            state.noteTypeNotes.loadState &&
            state.noteTypeNotes.loadState.status == RequestState.Pending,
        loadNoteTypeNotesSuccess:
            state.noteTypeNotes.loadState &&
            state.noteTypeNotes.loadState.status == RequestState.Success,
        loadNoteTypeNotesFailure:
            state.noteTypeNotes.loadState &&
            state.noteTypeNotes.loadState.status == RequestState.Failure,
        loadNoteTypeNotesErrorCode: state.noteTypeNotes.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialNotesPageProps> => {
    return {
        loadTrial: bindActionCreators(TrialActions.LoadTrialById, dispatch),
        clearTrial: bindActionCreators(TrialActions.Clear, dispatch),
        loadNoteTypeNotes: bindActionCreators(NoteTypesNotesActions.LoadNoteTypeNotesByTrialId, dispatch),
        clearNoteTypeNotes: bindActionCreators(NoteTypesNotesActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialNotesPage);
